// ============================================================================
// @gs/feedback/client — hooks TanStack Query (refetchInterval modeste, AUCUN websocket)
// ============================================================================
// Pattern useConsole.ts (Console UG V2) : @gs/api-client (retry 401 auto),
// `enabled` gere par l'app via le montage des composants. Capability-aware :
// 503 MIGRATION_116_PENDING -> bandeau ; 404 (route non montee) -> masquage.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getRegisteredAccessToken, errorFromResponse, ApiUnauthorizedError } from '@gs/api-client';
import {
  MAX_ATTACHMENT_BYTES,
  type CreateTicketInput,
  type PublishAnnouncementInput,
  type FeedbackStatus,
  type MessageDto,
  type TicketDto,
  type TriageInput,
} from '../schemas';

/** Le fil ouvert + la liste se rafraichissent doucement (spec §5 : pas de temps reel). */
export const FEEDBACK_REFETCH_MS = 30_000;

export interface TicketListResponse {
  staff: boolean;
  tickets: TicketDto[];
  /** Mig 293 appliquee : le filtre de nature s affiche (absent = serveur d avant). */
  announcements?: boolean;
  /** Le lecteur peut publier une nouveaute (personnel de l app, 293 appliquee). */
  canPublish?: boolean;
}

export interface TicketDetailResponse {
  ticket: TicketDto;
  staff: boolean;
  followers: Array<{ userId: string; name: string }>;
  statusHistory: Array<{ fromStatus: string | null; toStatus: string; byName: string; at: string }>;
  currentUserId: string;
}

export interface MessagesResponse {
  messages: MessageDto[];
  currentUserId: string;
  staff: boolean;
}

export interface AttachmentDto {
  id: string;
  parent: 'ticket' | 'message' | 'note';
  parentId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /** URL d'APP du contenu (CHORE-FBK-PJ-PROXY — plus de SAS navigateur) :
   *  Content-Disposition `inline` pour les previsualisables (image/PDF natif),
   *  `attachment` sinon. Bearer REQUIS — consommer via attachmentContent.ts
   *  (object URLs, cache de page sans expiration). */
  url: string;
  /** URL d'APP forcant le telechargement — offert pour TOUS les types. Bearer requis. */
  downloadUrl: string;
}

/** 503 MIGRATION_116_PENDING (module avant application de la mig — STOP David). */
export function isFeedbackMigrationPending(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | null;
  return e?.status === 503 || (e?.message ?? '').includes('MIGRATION_116_PENDING');
}

/** 404 = route /api/feedback non montee dans cette app (capability absente). */
export function isFeedbackUnavailable(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 404;
}

/**
 * Compteur de tickets non lus (badge nav « Commentaires » — UI_STANDARDS §4c
 * badge in-place). MEME prédicat participant que la liste ; l'acteur ne compte
 * pas ses propres événements (doctrine NTF). CAPABILITY-AWARE : 404 (route non
 * montée) / 503 (mig 116 pendante) => query en erreur, l'appelant n'affiche
 * simplement pas de badge — aucune erreur visible.
 */
export function useFeedbackUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: ['feedback', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/api/feedback/unread-count'),
    enabled,
    refetchInterval: FEEDBACK_REFETCH_MS,
    retry: false,
  });
}

export function useFeedbackTickets(enabled: boolean) {
  return useQuery({
    queryKey: ['feedback', 'tickets'],
    queryFn: () => api.get<TicketListResponse>('/api/feedback'),
    enabled,
    refetchInterval: FEEDBACK_REFETCH_MS,
    retry: false, // 503/404 capability : inutile de retenter
  });
}

export function useTicketDetail(ticketId: string | null) {
  return useQuery({
    queryKey: ['feedback', 'ticket', ticketId],
    queryFn: () => api.get<TicketDetailResponse>(`/api/feedback/${ticketId}`),
    enabled: !!ticketId,
    refetchInterval: FEEDBACK_REFETCH_MS,
    retry: false,
  });
}

export function useTicketMessages(ticketId: string | null) {
  return useQuery({
    queryKey: ['feedback', 'messages', ticketId],
    queryFn: () => api.get<MessagesResponse>(`/api/feedback/${ticketId}/messages`),
    enabled: !!ticketId,
    refetchInterval: FEEDBACK_REFETCH_MS,
    retry: false,
  });
}

export function useTicketNotes(ticketId: string | null, staff: boolean) {
  return useQuery({
    queryKey: ['feedback', 'notes', ticketId],
    queryFn: () =>
      api.get<Array<{ id: string; authorName: string; body: string; createdAt: string }>>(
        `/api/feedback/${ticketId}/notes`,
      ),
    enabled: !!ticketId && staff,
    retry: false,
  });
}

export function useTicketActivity(ticketId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['feedback', 'activity', ticketId],
    queryFn: () =>
      api.get<Array<{ id: string; action: string; byName: string; at: string; detail: string | null }>>(
        `/api/feedback/${ticketId}/activity`,
      ),
    enabled: !!ticketId && enabled,
    retry: false,
  });
}

export function useTicketAttachments(ticketId: string | null) {
  return useQuery({
    queryKey: ['feedback', 'attachments', ticketId],
    queryFn: () => api.get<AttachmentDto[]>(`/api/feedback/${ticketId}/attachments`),
    enabled: !!ticketId,
    retry: false,
  });
}

function invalidateTicket(qc: ReturnType<typeof useQueryClient>, ticketId: string) {
  void qc.invalidateQueries({ queryKey: ['feedback', 'tickets'] });
  void qc.invalidateQueries({ queryKey: ['feedback', 'ticket', ticketId] });
  void qc.invalidateQueries({ queryKey: ['feedback', 'messages', ticketId] });
  void qc.invalidateQueries({ queryKey: ['feedback', 'activity', ticketId] });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketInput) =>
      api.post<{ id: string; ticketNumber: string }>('/api/feedback', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', 'tickets'] }),
  });
}

/** Publier une nouveaute (feat-feedback-les-nouveautes-se-publient) : personnel de l app seulement,
 *  le serveur refiltre les invites. Les pieces jointes suivent par uploadAttachment. */
export function usePublishAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishAnnouncementInput) =>
      api.post<{ id: string; ticketNumber: string }>('/api/feedback/announcements', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', 'tickets'] }),
  });
}

export function useSendMessage(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; parentMessageId?: string; replyToId?: string; clientMsgId?: string; mentions?: string[] }) =>
      api.post<{ id: string }>(`/api/feedback/${ticketId}/messages`, input),
    onSuccess: () => invalidateTicket(qc, ticketId),
  });
}

export function useEditMessage(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; body: string }) =>
      api.patch<{ ok: true }>(`/api/feedback/${ticketId}/messages/${input.messageId}`, { body: input.body }),
    onSuccess: () => invalidateTicket(qc, ticketId),
  });
}

export function useDeleteMessage(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.delete<{ ok: true }>(`/api/feedback/${ticketId}/messages/${messageId}`),
    onSuccess: () => invalidateTicket(qc, ticketId),
  });
}

export function useToggleReaction(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; emoji: string; active: boolean }) =>
      api.post<{ ok: true }>(`/api/feedback/${ticketId}/messages/${input.messageId}/reactions`, {
        emoji: input.emoji,
        active: input.active,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', 'messages', ticketId] }),
  });
}

export function useTriage(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TriageInput) => api.patch<{ ok: true }>(`/api/feedback/${ticketId}`, input),
    onSuccess: () => invalidateTicket(qc, ticketId),
  });
}

/**
 * Changer le statut d'un ticket par son id (CHORE-FBK-LIFECYCLE) — archiver /
 * rouvrir EN UN GESTE depuis la LISTE, sans ouvrir le fil (le ticketId n'est pas
 * fixe a l'appel du hook, contrairement a useTriage). Reutilise le MEME PATCH
 * triage staff-only : le serveur trace StatusHistory + Activity + message
 * systeme. AUCUN endpoint neuf. Le 403 serveur garde le non-staff (defense en
 * profondeur — l'UI cache deja l'action).
 */
export function useSetTicketStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: FeedbackStatus }) =>
      api.patch<{ ok: true }>(`/api/feedback/${ticketId}`, { status }),
    onSuccess: (_data, { ticketId }) => invalidateTicket(qc, ticketId),
  });
}

export function useFollow(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (follow: boolean) =>
      api.post<{ ok: true }>(`/api/feedback/${ticketId}/follow`, { follow }),
    onSuccess: () => invalidateTicket(qc, ticketId),
  });
}

export function useAddNote(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.post<{ id: string }>(`/api/feedback/${ticketId}/notes`, { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feedback', 'notes', ticketId] }),
  });
}

function invalidateInbox(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['feedback', 'tickets'] });
  void qc.invalidateQueries({ queryKey: ['feedback', 'unread-count'] });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) => api.post<{ ok: true }>(`/api/feedback/${ticketId}/read`, {}),
    onSuccess: () => invalidateInbox(qc),
  });
}

/** Non-lu manuel (CHORE-FBK-INBOX-V2, pattern enveloppe Gmail) — le marqueur
 *  fbk.Reads est desactive cote serveur ; idempotent. */
export function useMarkUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) => api.post<{ ok: true }>(`/api/feedback/${ticketId}/unread`, {}),
    onSuccess: () => invalidateInbox(qc),
  });
}

/** Tout marquer comme lu (CHORE-FBK-INBOX-V2) — portee = ids fournis par
 *  l'appelant (tickets actuellement filtres/affiches). */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ticketIds: string[]) =>
      api.post<{ ok: true }>('/api/feedback/read-all', { ticketIds }),
    onSuccess: () => invalidateInbox(qc),
  });
}

/**
 * Upload binaire brut (POST /:id/attachments?filename=&parent=&parentId=).
 * fetch direct (PAS apiFetch — qui JSON.stringify le body) : File envoye tel
 * quel, Content-Type = type du fichier, Bearer via le getter enregistre.
 */
export async function uploadAttachment(
  ticketId: string,
  file: File,
  parent: 'ticket' | 'message' | 'note' = 'ticket',
  parentId?: string,
): Promise<{ id: string }> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error('ATTACHMENT_TOO_LARGE');
  const getToken = getRegisteredAccessToken();
  if (!getToken) throw new ApiUnauthorizedError('Non authentifie — getAccessToken non enregistre');
  const token = await getToken();
  const qs = new URLSearchParams({ filename: file.name, parent });
  if (parentId) qs.set('parentId', parentId);
  const res = await fetch(`/api/feedback/${ticketId}/attachments?${qs.toString()}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) throw await errorFromResponse(res);
  return res.json() as Promise<{ id: string }>;
}
